/**
 * MEVIA - Subscription Context
 * Provides subscription permissions throughout the app
 * Controls access to features based on the company's subscription plan
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPermissions = useCallback(async () => {
    // Super admins have all permissions
    if (user?.rol === 'super_admin' || user?.rol === 'superadmin') {
      setPermissions({
        has_active_subscription: true,
        plan_name: 'Super Admin',
        is_trial: false,
        permissions: {
          culture: true,
          personality: true,
          integrated_report: true,
          hr_indicators: true,
          alerts: true,
          action_plans: true,
          evaluation_periods: true,
          profile_evaluation: true,
          corporate_identity: true,
          pdf_export: true,
          candidate_evaluation: true,
          basic_reports: true,
          advanced_reports: true,
          api_access: true,
          priority_support: true,
          nom035: true,
          orgchart: true,
          pulse_surveys: true,
          evaluation_cycles: true,
          dashboard_central: true,
          people_analytics: true,
          company_profile: true,
          compatibility_4d: true,
          strategic_plans: true
        },
        limits: {
          max_evaluations: -1,
          max_employees: -1,
          evaluations_used: 0
        }
      });
      setLoading(false);
      return;
    }

    // If user has a company, fetch their permissions
    if (user?.empresa_id) {
      try {
        const response = await axios.get(`/subscriptions/permissions/${user.empresa_id}`);
        if (response.data.success) {
          setPermissions(response.data.data);
        }
      } catch (error) {
        logger.error('Error loading subscription permissions:', error);
        // Default to minimal permissions on error
        setPermissions({
          has_active_subscription: false,
          plan_name: null,
          permissions: {
            culture: false,
            personality: false,
            integrated_report: false,
            hr_indicators: false,
            alerts: false,
            action_plans: false,
            evaluation_periods: false,
            profile_evaluation: false,
            corporate_identity: false,
            pdf_export: false,
            candidate_evaluation: false,
            basic_reports: false,
            advanced_reports: false,
            api_access: false,
            priority_support: false,
            // MEVIA v5.0 modules
            evaluation_cycles: false,
            nom035: false,
            pulse_surveys: false,
            company_profile: false,
            compatibility_4d: false,
            strategic_plans: false,
            dashboard_central: false,
            evolution_impact: false
          },
          limits: {
            max_evaluations: 0,
            max_employees: 0,
            evaluations_used: 0
          }
        });
      }
    } else {
      // No company associated - individual user gets basic permissions
      setPermissions({
        has_active_subscription: true,
        plan_name: 'Individual',
        is_trial: false,
        permissions: {
          culture: true,
          personality: true,
          integrated_report: false,
          hr_indicators: false,
          alerts: false,
          action_plans: false,
          evaluation_periods: false,
          profile_evaluation: false,
          corporate_identity: false,
          pdf_export: true,
          candidate_evaluation: false,
          basic_reports: true,
          advanced_reports: false,
          api_access: false,
          priority_support: false,
          // MEVIA v5.0 modules
          evaluation_cycles: false,
          nom035: false,
          pulse_surveys: false,
          company_profile: false,
          compatibility_4d: false,
          strategic_plans: false,
          dashboard_central: true,
          evolution_impact: false
        },
        limits: {
          max_evaluations: -1,
          max_employees: 0,
          evaluations_used: 0
        }
      });
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (user) {
      loadPermissions();
    } else {
      setPermissions(null);
      setLoading(false);
    }
  }, [user, loadPermissions]);

  /**
   * Check if a specific module/feature is enabled
   * @param {string} moduleKey - The module key to check (e.g., 'culture', 'action_plans')
   * @returns {boolean}
   */
  const hasAccess = (moduleKey) => {
    if (!permissions) return false;
    if (!permissions.has_active_subscription) return false;
    return permissions.permissions[moduleKey] === true;
  };

  /**
   * Check if the company can create more evaluations
   * @returns {boolean}
   */
  const canCreateEvaluation = () => {
    if (!permissions) return false;
    if (!permissions.has_active_subscription) return false;
    const { max_evaluations, evaluations_used } = permissions.limits;
    if (max_evaluations === -1) return true; // Unlimited
    return evaluations_used < max_evaluations;
  };

  /**
   * Get remaining evaluations
   * @returns {number} - Remaining evaluations or -1 for unlimited
   */
  const getRemainingEvaluations = () => {
    if (!permissions) return 0;
    const { max_evaluations, evaluations_used } = permissions.limits;
    if (max_evaluations === -1) return -1;
    return Math.max(0, max_evaluations - evaluations_used);
  };

  /**
   * Refresh permissions from server
   */
  const refreshPermissions = () => {
    loadPermissions();
  };

  const value = {
    permissions,
    loading,
    hasAccess,
    canCreateEvaluation,
    getRemainingEvaluations,
    refreshPermissions,
    planName: permissions?.plan_name || null,
    isTrialPlan: permissions?.is_trial || false,
    hasActiveSubscription: permissions?.has_active_subscription || false
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
